import clsx from 'clsx';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import ImageServer from '@/modules/common/components/ImageServer';
import { GridLayoutBorder } from '@/modules/common/components/icons/GridLayoutBorder';
import Link from '@/modules/common/components/ui/Link';
import { ShopByDepartmentItemFragment } from '@/modules/product/gql/__generated/shopByDepartmentCollection.generated';
import { DataLayerFeature } from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { selectContentDataLayerEvent } from '@/modules/product/services/data-layer-events/select_content';

import { IconChevronRight16 } from '../icons/IconChevronRight16';

const altVariantWidth = 100;

export type SampleDepartmentCardProps = {
  className?: string;
  imageClassName?: string;
  variant?: 'default' | 'nav' | 'all';
  onClick?: () => void;
  feature?: DataLayerFeature;
  contentType?: string;
  contentId?: string;
  where?: DataLayerWhere;
  fetchPriority?: 'high' | 'low' | 'auto';
} & ShopByDepartmentItemFragment;

export const SamplesDepartmentCard = ({
  className,
  title,
  slug,
  thumbnail,
  imageClassName,
  variant = 'default',
  onClick = () => {},
  feature,
  contentType = 'Quick link',
  contentId = '',
  where,
  fetchPriority,
}: SampleDepartmentCardProps) => {
  const isFaucet = title?.toLowerCase()?.includes('faucet');

  return (
    <Link
      href={variant === 'all' ? '/samples/categories' : `/samples/catalog/department/${slug}`}
      className={clsx(
        'relative h-full w-full rounded-xxl',
        variant === 'default' ?
          'relative flex aspect-square flex-col items-start justify-end overflow-hidden px-4 py-4 md:px-6'
        : 'block text-center',
        className
      )}
      data-testid="department-link"
      onClick={() => {
        selectContentDataLayerEvent({
          contentType,
          feature,
          contentId,
          where,
          name: title || '',
        });
        onClick();
      }}
    >
      {variant === 'all' ?
        <GridLayoutBorder
          style={{
            padding: '25px',
            height: `${altVariantWidth}px`,
            width: `${altVariantWidth}px`,
          }}
          className="mb-3 rounded-full border border-gray-100 transition-transform duration-700 hover:scale-110"
        />
      : thumbnail?.url ?
        <div
          className={clsx(
            'overflow-hidden bg-gray-70 transition-transform duration-700 hover:scale-110',
            variant === 'default' ?
              'absolute bottom-0 left-0 right-0 top-0'
            : `w-[${altVariantWidth}px] h-[${altVariantWidth}px] mb-3 aspect-square rounded-full`
          )}
        >
          <ImageServer
            src={thumbnail.url}
            alt={thumbnail.title || `Thumbnail for ${title}`}
            width={
              variant !== 'default' ? altVariantWidth + 45
              : thumbnail?.width ?
                thumbnail.width
              : 264
            }
            className={clsx(
              'pointer-events-none relative w-full mix-blend-darken',
              imageClassName,
              variant !== 'default' && '-ml-[10px] h-[145px] min-w-[120px]',
              isFaucet && variant !== 'default' && '-ml-[30px]'
            )}
            data-testid="department-image"
            fetchPriority={fetchPriority}
          />
        </div>
      : null}

      {variant === 'default' ?
        <div className="relative z-10">
          <h2 className="text-base font-bold leading-[22px]" data-testid="department-title">
            {title}
          </h2>
          <p
            className="mt-1 flex items-center text-gray-500"
            data-testid={`${title}-department-explore`}
          >
            Explore <IconChevronRight16 />
          </p>
        </div>
      : <h2 className="text-[13px] font-bold leading-4">{title}</h2>}
    </Link>
  );
};
